<template>
  <div
    class="search-box"
    :class="{ 'search-box--focused': isFocused, 'search-box--single': singleRow, 'search-box--borderless': borderless }"
    ref="searchBoxRef"
  >
    <!-- Single-row variant (flag-on): [entity][query][xpac pill][⋮ kebab].
         two-deck (#681 landing): same children re-laid on a grid — row 1 = bare
         input, row 2 = entity selector left / pill + kebab + Search right. -->
    <div v-if="singleRow" class="search-row-single" :class="twoDeck ? 'search-row-deck' : 'd-flex align-center'">
      <entity-selector-button v-if="!thinking" class="ml-1 entity-slot" @entity-selected="focusSearchInput" />
      <!-- The homepage question waits here for its query (`nl_search`, #1744), so the page
           that opens already shows the answer, not a few seconds of all works. -->
      <div v-else class="nl-wait" role="status">
        <span class="nl-dots"><span /><span /><span /></span>
        <span>
          Writing the query{{ thinkElapsed >= 2 ? ` · ${thinkElapsed} s` : '' }}
          <span v-if="thinkElapsed >= 15" class="nl-wait-more">Big questions can take up to half a minute.</span>
        </span>
      </div>
      <textarea
        ref="inputRef"
        v-model="searchString"
        class="search-input flex-grow-1"
        :placeholder="placeholder"
        aria-label="Search scholarly works"
        :autofocus="autofocus"
        :readonly="thinking"
        rows="1"
        @keydown.enter.prevent="onEnter"
        @keydown.down.prevent="onArrowDown"
        @keydown.up.prevent="onArrowUp"
        @keydown.escape="onEscape"
        @input="onTextareaInput"
        @paste="onTextareaPaste"
        @focus="onFocus"
        @blur="onBlur"
        autocomplete="off"
      />
      <v-btn
        v-if="searchString && !thinking"
        icon
        variant="text"
        size="small"
        class="clear-btn"
        aria-label="Clear search"
        @click="clearSearch"
      >
        <v-icon size="16">mdi-close</v-icon>
      </v-btn>

      <!-- xpac pill: only for works + only when xpac is on; click OPENS the kebab
           (no quick-off — toggling lives inside the kebab). Gray, not black — it's
           an indicator, not a primary action (#440 r5). -->
      <v-btn
        v-if="isWorksEntity && isXpacEnabled"
        variant="tonal"
        color="grey-darken-2"
        class="text-none xpac-pill mr-1"
        size="small"
        rounded
        @click="kebabOpen = true"
      >
        xpac
      </v-btn>

      <!-- The single-row ⋮ kebab: holds every former row-2 option. -->
      <v-menu v-model="kebabOpen" location="bottom end" :close-on-content-click="false">
        <template #activator="{ props: kebabProps }">
          <v-btn
            v-bind="kebabProps"
            icon
            variant="text"
            size="small"
            class="control-btn kebab-slot"
            aria-label="Search options"
          >
            <v-icon size="20">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <!-- #598 r1 (Jason): narrower list (~75% of the old content-stretched width)
             so the right-edge controls read against their row title; subtitles may
             wrap to 2 lines. The Stemming SECTION is gone — stemming is now a single
             toggle row at the bottom next to xpac (toggles = independent switches;
             checkmarks stay for the pick-one groups). -->
        <v-list v-if="isWorksEntity" density="compact" class="search-kebab-list" min-width="260" max-width="290">
          <template v-if="searchMode !== 'semantic'">
            <v-list-item @click="setField('title')">
              <v-list-item-title>Title</v-list-item-title>
              <template #append>
                <v-icon v-if="searchField === 'title'" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>
            <v-list-item @click="setField('title_and_abstract')">
              <v-list-item-title>Title &amp; abstract</v-list-item-title>
              <template #append>
                <v-icon v-if="searchField === 'title_and_abstract'" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>
            <v-list-item @click="setField('title_abstract_keywords')">
              <v-list-item-title>Title, abstract, &amp; keywords</v-list-item-title>
              <template #append>
                <v-icon v-if="searchField === 'title_abstract_keywords'" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>
            <v-list-item @click="setField('all')">
              <v-list-item-title>Title, abstract, fulltext, &amp; keywords</v-list-item-title>
              <template #append>
                <v-icon v-if="searchField === 'all'" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>
            <v-divider class="my-1" />
          </template>

          <v-list-item @click="setMode('term')">
            <v-list-item-title>Boolean</v-list-item-title>
            <v-list-item-subtitle class="menu-subtitle">Keyword search with operators</v-list-item-subtitle>
            <template #append>
              <v-icon v-if="searchMode === 'term'" class="check-icon">mdi-check</v-icon>
            </template>
          </v-list-item>
          <v-list-item @click="setMode('semantic')">
            <v-list-item-title>Semantic (beta)</v-list-item-title>
            <v-list-item-subtitle class="menu-subtitle">AI-powered meaning search</v-list-item-subtitle>
            <template #append>
              <v-icon v-if="searchMode === 'semantic'" class="check-icon">mdi-check</v-icon>
            </template>
          </v-list-item>

          <v-divider class="my-1" />
          <!-- Independent on/off options: toggle switches, not checkmarks (#598 r1).
               The whole row is the click target; the switch itself is inert
               (pointer-events none) so a tap on it can't double-fire. -->
          <v-list-item @click="toggleXpac">
            <v-list-item-title>Include expanded index</v-list-item-title>
            <v-list-item-subtitle class="menu-subtitle">
              192M works from lower-quality sources (xpac)
            </v-list-item-subtitle>
            <template #append>
              <v-switch
                :model-value="isXpacEnabled"
                class="kebab-switch ml-3"
                density="compact"
                color="grey-darken-4"
                hide-details
                inset
                readonly
              />
            </template>
          </v-list-item>
          <v-list-item v-if="searchMode !== 'semantic'" @click="disableStemming(!stemmingDisabled)">
            <v-list-item-title>Enable stemming</v-list-item-title>
            <v-list-item-subtitle class="menu-subtitle">
              "Run" also matches "runner," "running," etc
            </v-list-item-subtitle>
            <template #append>
              <v-switch
                :model-value="!stemmingDisabled"
                class="kebab-switch ml-3"
                density="compact"
                color="grey-darken-4"
                hide-details
                inset
                readonly
              />
            </template>
          </v-list-item>
        </v-list>
        <!-- Non-works: sparse kebab (no works-only search options). -->
        <v-list v-else density="compact" min-width="220">
          <v-list-item disabled>
            <v-list-item-title class="text-medium-emphasis">No search options</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- Magnifier = submit button, at the right edge of the box (#440 r5).
           hideSubmit drops it on the landing page, where a standalone "Search"
           call-to-action button below the box takes over (#681). -->
      <v-btn
        v-if="!hideSubmit"
        icon
        variant="text"
        size="small"
        class="control-btn mr-1"
        aria-label="Search"
        @click="clickSubmit"
      >
        <v-icon size="20">mdi-magnify</v-icon>
      </v-btn>

      <!-- two-deck submit (#681 landing): black in-box Search button, row 2 right -->
      <button v-if="twoDeck" type="button" class="deck-submit" :disabled="thinking" @click="clickSubmit">
        <v-progress-circular v-if="thinking" indeterminate size="14" width="2" aria-label="Writing the query" />
        <template v-else>Search</template>
      </button>
    </div>

    <!-- Row 1: Input + clear (two-row variant, flag-off) -->
    <div v-if="!singleRow" class="search-row-1 d-flex align-center">
      <textarea
        ref="inputRef"
        v-model="searchString"
        class="search-input flex-grow-1"
        :placeholder="placeholder"
        aria-label="Search scholarly works"
        :autofocus="autofocus"
        rows="1"
        @keydown.enter.prevent="onEnter"
        @keydown.down.prevent="onArrowDown"
        @keydown.up.prevent="onArrowUp"
        @keydown.escape="onEscape"
        @input="onTextareaInput"
        @paste="onTextareaPaste"
        @focus="onFocus"
        @blur="onBlur"
        autocomplete="off"
      />
      <v-btn
        v-if="searchString"
        icon
        variant="text"
        size="small"
        class="mr-2"
        aria-label="Clear search"
        @click="clearSearch"
      >
        <v-icon size="16">mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- Autocomplete dropdown -->
    <div v-if="showDropdown" class="autocomplete-dropdown">
      <div class="autocomplete-list">
        <div
          v-for="(item, index) in suggestions"
          :key="item.id + '-' + item._acType"
          class="autocomplete-item d-flex align-center"
          :class="{ 'autocomplete-item--highlighted': index === highlightedIndex }"
          @mousedown.prevent="selectSuggestion(item)"
        >
          <v-icon size="16" class="autocomplete-item-icon">{{ item._icon }}</v-icon>
          <div class="autocomplete-item-name">{{ item.display_name }}</div>
        </div>
      </div>
      <!-- Footer = the full SERP search, which is what a bare Enter does (#820 r3).
           Normally "Search works for …"; when every suggestion is one of
           authors/sources/institutions it swaps to that type, so e.g. a typed
           person's name puts the full ranked author-profile list one Enter away
           (the CNRS Q5.4 "my works are missing" fix, generalized). -->
      <div class="autocomplete-footer d-flex align-center" @mousedown.prevent="submitFooter">
        <v-icon size="16" class="autocomplete-item-icon">mdi-magnify</v-icon>
        <div class="autocomplete-footer-text">
          Search {{ footerEntityType }} for <em>{{ searchString.trim() }}</em>
        </div>
        <span class="enter-keycap">enter</span>
      </div>
    </div>

    <!-- Divider -->
    <div v-if="showRow2 && !singleRow" class="search-divider"></div>

    <!-- Row 2: Entity selector (left) | spacer | Combined menu (right) | Xpac toggle (far right) -->
    <div v-if="showRow2 && !singleRow" class="search-row-2 d-flex align-center">
      <entity-selector-button @entity-selected="focusSearchInput" />

      <v-spacer />

      <template v-if="isWorksEntity">
        <!-- Search options menu (fields + stemming) — hidden when semantic -->
        <v-menu v-if="searchMode !== 'semantic'" v-model="optionsMenuOpen" location="bottom end" :close-on-content-click="false">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              variant="text"
              size="small"
              class="control-btn"
              aria-label="Search options"
            >
              <v-icon size="18">mdi-tune-variant</v-icon>
            </v-btn>
          </template>
          <v-list density="compact" min-width="280">
            <v-list-subheader>Search fields</v-list-subheader>

            <v-list-item @click="setField('title'); optionsMenuOpen = false">
              <v-list-item-title>Title</v-list-item-title>
              <template #append>
                <v-icon v-if="searchField === 'title'" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>

            <v-list-item @click="setField('title_and_abstract'); optionsMenuOpen = false">
              <v-list-item-title>Title & abstract</v-list-item-title>
              <template #append>
                <v-icon v-if="searchField === 'title_and_abstract'" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>

            <v-list-item @click="setField('title_abstract_keywords'); optionsMenuOpen = false">
              <v-list-item-title>Title, abstract, & keywords</v-list-item-title>
              <template #append>
                <v-icon v-if="searchField === 'title_abstract_keywords'" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>

            <v-list-item @click="setField('all'); optionsMenuOpen = false">
              <v-list-item-title>Title, abstract, fulltext, & keywords</v-list-item-title>
              <template #append>
                <v-icon v-if="searchField === 'all'" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>

            <v-divider class="my-1" />

            <v-list-subheader>Stemming</v-list-subheader>

            <v-list-item @click="disableStemming(false)">
              <v-list-item-title>Enable stemming</v-list-item-title>
              <v-list-item-subtitle class="menu-subtitle">looking = look, looker, etc</v-list-item-subtitle>
              <template #append>
                <v-icon v-if="!stemmingDisabled" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>

            <v-list-item @click="disableStemming(true)">
              <v-list-item-title>Disable stemming</v-list-item-title>
              <template #append>
                <v-icon v-if="stemmingDisabled" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Search strategy menu (semantic search toggle) -->
        <v-menu v-model="strategyMenuOpen" location="bottom end">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="text"
              class="control-btn text-none ml-1"
              size="small"
            >
              <span class="control-label">{{ searchMode === 'semantic' ? 'semantic' : 'boolean' }}</span>
              <v-icon size="12" class="ml-1">mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item @click="setMode('term')">
              <v-list-item-title>Boolean</v-list-item-title>
              <v-list-item-subtitle class="menu-subtitle">Keyword search with operators</v-list-item-subtitle>
              <template #append>
                <v-icon v-if="searchMode === 'term'" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>
            <v-list-item @click="setMode('semantic')">
              <v-list-item-title class="d-flex align-center">
                Semantic
                <v-chip size="x-small" class="ml-2" color="grey-darken-1" variant="tonal">beta</v-chip>
              </v-list-item-title>
              <v-list-item-subtitle class="menu-subtitle">
                AI-powered meaning search
              </v-list-item-subtitle>
              <template #append>
                <v-icon v-if="searchMode === 'semantic'" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Xpac toggle -->
        <v-tooltip location="bottom" max-width="300" aria-label="Toggle expansion pack works">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              :variant="isXpacEnabled ? 'flat' : 'text'"
              :color="isXpacEnabled ? '#374151' : undefined"
              class="text-none ml-1"
              :class="{ 'control-btn': !isXpacEnabled }"
              size="small"
              rounded
              @click="toggleXpac"
            >
              <span class="control-label" :style="isXpacEnabled ? 'color: white' : ''">xpac</span>
            </v-btn>
          </template>
          <span>Include 'expansion pack' works. Better coverage (192M new works from DataCite and repositories), but data quality is lower.</span>
        </v-tooltip>
      </template>
    </div>
    <div v-if="dbSearchError" class="db-search-error text-error text-body-2 px-3 py-2" role="alert">{{ dbSearchError }}</div>
  </div>
</template>

<script setup>
defineOptions({ name: 'SearchBox' });

import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { pickOql } from '@/oqlForm';
import { useDisplay } from 'vuetify';
import { debounce } from 'lodash-es';
import { getEntityConfig, getEntityConfigs } from '@/entityConfigs';
import { api } from '@/api';
import { createSimpleFilter, filtersFromUrlStr, filtersAsUrlStr } from '@/filterConfigs';
import { url } from '@/url';
import { facetConfigs } from '@/facetConfigs';
import { edgeOn, edgeSupports, edgeRows, edgeWarm } from '@/edgeAutocomplete';
import { extractIssn, extractLocationId, extractOpenalexId, hasUnquotedWildcard, looksLikeOql, looksLikeQuestion, requestSearchBoxFocus, consumeSearchBoxFocus, authorNameMatchesQuery, dedupeByName, footerSearchEntityType } from '@/components/searchBox.helpers';
import { ensureIntentModel, classifyQuery } from '@/intent/useIntent';
import { waitForApiKey } from '@/keyWait';
import { answerQuestion, oqlForPageUrl, saveThread } from '@/components/NlSearch/nlChat';
import { validateOql } from '@/components/OqlPlayground/oqlEditorApi';
import { translateSearch } from '@/dbSearchTranslate';
import { rememberTranslation } from '@/dbSearchNotes';
import { entityCounts, worksCoreCount, compactCount } from '@/entityCounts';
import { getShortId } from '@/openalexId';
import EntitySelectorButton from '@/components/EntitySelectorButton.vue';

const props = defineProps({
  autofocus: Boolean,
  showExamples: Boolean,
  // Flag-on (OqlSerp) layout: collapse the two-row box into one row
  // `[entity][query][⋮ kebab]`, moving every row-2 option (fields/stemming/
  // strategy/xpac) into the kebab and showing an xpac pill only when xpac is on.
  // Default false → today's two-row box (flag-off path) is byte-for-byte unchanged.
  singleRow: Boolean,
  // #681 landing: lay the single-row children out as two decks (input over controls)
  twoDeck: Boolean,
  // Embedded variant: drop the box's own border/radius/background so it can sit as
  // the (white) body of a host card — e.g. the SERP Basic-mode search card, where
  // the box is the card body and the filter chips are the card footer (#440 r3).
  borderless: Boolean,
  // Landing page (#681): hide the in-box magnifier submit button. A standalone
  // "Search" button below the box drives submission instead (via exposed submit()).
  hideSubmit: Boolean,
  // Landing page (#681): override the works placeholder with fixed copy instead of
  // the live count (the count is confusing next to the xpac-inclusive "500M" claim).
  placeholderOverride: String,
});

// --- Identifier extraction helpers ---

function extractDoi(str) {
  if (!str) return null;
  const trimmed = str.trim();

  // URL form: https://doi.org/10.xxx or https://dx.doi.org/10.xxx (http too)
  const urlMatch = trimmed.match(/^https?:\/\/(?:dx\.)?doi\.org\/(10\..+)$/i);
  if (urlMatch) return `https://doi.org/${urlMatch[1]}`;

  // Labeled form: "DOI: 10.xxx" or "doi:10.xxx"
  const labelMatch = trimmed.match(/^doi:\s*(10\..+)$/i);
  if (labelMatch) return `https://doi.org/${labelMatch[1]}`;

  // Bare DOI: starts with 10. and contains a slash
  if (/^10\.\d{4,}/.test(trimmed) && trimmed.includes('/')) {
    return `https://doi.org/${trimmed}`;
  }

  return null;
}

function extractOrcid(str) {
  if (!str) return null;
  const trimmed = str.trim();

  // URL form: https://orcid.org/0000-...
  const urlMatch = trimmed.match(/^https?:\/\/orcid\.org\/(\d{4}-\d{4}-\d{4}-\d{3}[\dX])$/i);
  if (urlMatch) return `https://orcid.org/${urlMatch[1].toUpperCase()}`;

  // Labeled form: "ORCID: 0000-..." or "orcid:0000-..."
  const labelMatch = trimmed.match(/^orcid:\s*(\d{4}-\d{4}-\d{4}-\d{3}[\dX])$/i);
  if (labelMatch) return `https://orcid.org/${labelMatch[1].toUpperCase()}`;

  // Bare ORCID: exactly the 4-4-4-4 pattern
  const bareMatch = trimmed.match(/^(\d{4}-\d{4}-\d{4}-\d{3}[\dX])$/i);
  if (bareMatch) return `https://orcid.org/${bareMatch[1].toUpperCase()}`;

  return null;
}

async function tryIdentifierLookup() {
  const input = searchString.value;

  // Locations record lookup (oxjob #852) — locations-SERP-scoped: a pasted
  // repository record ID (doi:…, pmh:…, mag:…, a bare DOI, or a full location
  // URL) jumps straight to that location's page, verified against the
  // singleton first so a lookalike falls through to a normal search. Returns
  // false either way so the works-DOI branch below can't hijack a locations
  // lookup to a work page.
  if (entityType.value === 'locations') {
    const locId = extractLocationId(input);
    if (locId) {
      try {
        const resp = await api.getEntity(`locations/${locId}`);
        if (resp && resp.id) {
          searchString.value = '';
          dismissDropdown();
          // Component ids are slash-bearing — route by path, not named route
          // (matches filters.js entityPageTarget).
          router.push({ path: `/locations/${locId}` });
          return true;
        }
      } catch (e) {
        // Not found or API error — fall through to a regular search.
      }
    }
    return false;
  }

  const doi = extractDoi(input);
  if (doi) {
    try {
      const resp = await api.get('/works', { filter: `doi:${doi}`, per_page: 1, select: 'id' });
      if (resp.results && resp.results.length > 0) {
        const entityId = resp.results[0].id?.replace('https://openalex.org/', '') || resp.results[0].id;
        searchString.value = '';
        dismissDropdown();
        router.push({ name: 'EntityPage', params: { entityType: 'works', entityId } });
        return true;
      }
    } catch (e) {
      // DOI not found or API error — fall through to regular search
    }
    return false;
  }

  const orcid = extractOrcid(input);
  if (orcid) {
    try {
      const resp = await api.get('/authors', { filter: `orcid:${orcid}`, per_page: 1, select: 'id' });
      if (resp.results && resp.results.length > 0) {
        searchString.value = '';
        dismissDropdown();
        const currentFilters = filtersFromUrlStr('works', route.query.filter);
        const newFilter = createSimpleFilter('works', 'authorships.author.id', resp.results[0].id);
        url.pushNewFilters([...currentFilters, newFilter], 'works');
        return true;
      }
    } catch (e) {
      // ORCID not found or API error — fall through to regular search
    }
    return false;
  }

  // A pasted/typed OpenAlex entity ID (e.g. an author "A5017453014", or a full
  // openalex.org URL) jumps straight to that entity's page (zd#8363). Verify it
  // resolves before navigating, so an ID-shaped string that isn't a real entity
  // falls through to a normal search.
  const oaId = extractOpenalexId(input);
  if (oaId) {
    try {
      const resp = await api.getEntity(oaId.normalized);
      if (resp && resp.id) {
        const entityId = resp.id.replace('https://openalex.org/', '');
        searchString.value = '';
        dismissDropdown();
        router.push({ name: 'EntityPage', params: { entityType: oaId.entityType, entityId } });
        return true;
      }
    } catch (e) {
      // ID not found or API error — fall through to regular search
    }
    return false;
  }

  return false;
}

// --- OQL paste/type auto-detection (oxjob #598) ---
// A user handed an OQL snippet (docs, a colleague) pastes it into the Basic
// search box; running it as a literal text search returns garbage. Sniff for
// OQL shapes (cheap, local), confirm with the server /validate (the truth —
// only a VALID parse acts), then SILENTLY route to the OQL tab with the
// canonical query loaded. Session mode override only — an auto-detect is not
// an explicit mode choice, so the durable `serpMode` pref is untouched.
// Gated to the single-row (flag-on) variant, so flag-off surfaces are inert.
let oqlProbeSeq = 0;
async function tryOqlRoute() {
  if (!props.singleRow || !store.getters.featureFlags['oql']) return false;
  const text = searchString.value;
  if (!looksLikeOql(text)) return false;
  const seq = ++oqlProbeSeq;
  let res;
  try {
    res = await validateOql(text.trim());
  } catch (e) {
    return false; // validate unreachable — fall through to a normal search
  }
  // Stale probe: the box changed (more typing, a clear, a route sync) while the
  // validate round-trip was in flight — never act on the old text.
  if (seq !== oqlProbeSeq || searchString.value !== text) return false;
  if (!res?.valid || !res.oql) return false;
  searchString.value = '';
  dismissDropdown();
  store.commit('setSerpModeOverride', 'oql');
  url.pushToRoute(router, {
    name: 'OqlQuery',
    query: { oql: url.oqlForUrl(pickOql(res, store.getters.newOql)) },   // oxjob #1555
  });
  return true;
}

// A search strategy pasted from PubMed, Scopus, Web of Science, Ovid, Embase, Cochrane or EBSCO (oxjob #1659): as a
// literal text search it returns 0 results with no warning ([tiab], TITLE-ABS-KEY). Translate it locally (no model,
// no request) and open it in the OQL tab, with the differences listed above it (DbSearchNotes). One we can't read
// (unbalanced brackets, a missing search line) stays in the box with the reason. Plain Boolean strings return null
// here and search as before. Same gate as tryOqlRoute.
const dbSearchError = ref('');
function tryDatabaseSearchRoute() {
  if (!props.singleRow || !store.getters.featureFlags['oql']) return false;
  if (entityType.value !== 'works') return false;
  const text = searchString.value;
  const result = translateSearch(text);
  if (!result) return false;
  if (!result.oql) {
    dbSearchError.value = `This looks like a ${result.sourceLabel} search, but OpenAlex couldn't read it: ${result.error.message} Fix it and press Enter, or remove the field codes to search the words.`;
    dismissDropdown();
    return true;
  }
  dbSearchError.value = '';
  const entry = rememberTranslation(result, text);
  searchString.value = '';
  dismissDropdown();
  store.commit('setSerpModeOverride', 'oql');
  url.pushToRoute(router, { name: 'OqlQuery', query: { oql: entry.oql } });
  return true;
}

const store = useStore();
const route = useRoute();
const router = useRouter();
const { smAndDown } = useDisplay();

const inputRef = ref(null);
const searchBoxRef = ref(null);
const searchString = ref('');
const searchMode = ref('term'); // 'term' or 'semantic'
// Default scope since oxjob #1521: title + abstract text OR the keywords a query phrase names.
const searchField = ref('title_abstract_keywords'); // 'all' | 'title' | 'title_and_abstract' | 'title_abstract_keywords'
const stemmingDisabled = ref(false);
const isFocused = ref(false);
const strategyMenuOpen = ref(false);
const optionsMenuOpen = ref(false);
// Single-row kebab open state — also opened by clicking the xpac pill (no quick-off).
const kebabOpen = ref(false);

// Autocomplete state
const suggestions = ref([]);
const highlightedIndex = ref(-1);
const dropdownOpen = ref(false);
const isUserTyping = ref(false);
// The new search (`nl_search`, oxjob #1744): once the homepage text reads as a question it goes
// to the chat, so keyword suggestions under a sentence only get in the way. This one switch hides
// the dropdown, stops fetching, and lets Enter submit the typed text; shorter text turns it back on.
// Names on screen that the typed text still prefixes keep it on ("university of california san").
const suggestionsOff = computed(() => store.getters.nlSearch && route.name === 'Home' &&
  looksLikeQuestion(searchString.value,
    suggestions.value.filter(s => s._acType !== 'works').map(s => s.display_name)));
const showDropdown = computed(() => dropdownOpen.value && suggestions.value.length > 0 && !suggestionsOff.value);
// What the typed string looks like (oxjob #1347): {label, confidence} from the in-browser
// classifier, recomputed synchronously on every keystroke; null until its weights load.
const intent = ref(null);
// The dropdown footer's target (#820 r3): the current entity type, swapped to
// authors/sources/institutions when EVERY visible suggestion is that one type — unless a
// confident intent says the string is a works query (boolean, title, ...) or names an entity type.
const footerEntityType = computed(() =>
  footerSearchEntityType(suggestions.value.map(s => s._acType), entityType.value, intent.value));
const showRow2 = ref(true);
const isWorksEntity = computed(() => entityType.value === 'works');

// Entity type: read from route on Serp, store elsewhere
const entityType = computed({
  get() {
    if (route.name === 'Serp' && route.params.entityType) {
      return route.params.entityType;
    }
    return store.state.entityType;
  },
  set(to) {
    url.pushToRoute(router, {
      name: 'Serp',
      params: { entityType: to },
    });
  },
});

// Xpac toggle (route query driven). READS both corpus vocabularies (a
// `?corpus=all|expansion` route shows as enabled, #763) but still WRITES the
// legacy `?include_xpac=true` when enabling: the is_xpac facet workflow
// (GroupBySidebar/ActionMenu/AddFilter gates) is only legal under the legacy
// vocabulary — the API 400s a `filter=is_xpac:` on a `corpus=` route.
// Migrating that whole workflow to the corpus vocabulary is follow-up work.
const isXpacEnabled = computed(() => url.xpacIncludedInRoute(route.query));

function toggleXpac() {
  const newQuery = { ...route.query };
  delete newQuery.include_xpac;
  delete newQuery.corpus;
  if (!isXpacEnabled.value) {
    newQuery.include_xpac = 'true';
  }
  url.pushToRoute(router, {
    name: route.name,
    params: route.params,
    query: newQuery,
  });
}

// Map searchMode + searchField + stemmingDisabled → URL search param type
const resolvedSearchType = computed(() => {
  if (!isWorksEntity.value) return 'search';
  if (searchMode.value === 'semantic') return 'search.semantic';

  // Force the no-stem (exact) field when stemming is off OR the query has a wildcard.
  const useExact = stemmingDisabled.value || hasUnquotedWildcard(searchString.value);

  const fieldMap = {
    all: useExact ? 'search.exact' : 'search',
    title: useExact ? 'search.title.exact' : 'search.title',
    title_and_abstract: useExact ? 'search.title_and_abstract.exact' : 'search.title_and_abstract',
    title_abstract_keywords: useExact ? 'search.title_abstract_keywords.exact' : 'search.title_abstract_keywords',
  };
  return fieldMap[searchField.value] || 'search';
});

const placeholder = computed(() => {
  // #681: landing page pins a fixed works placeholder (no count) via prop.
  if (props.placeholderOverride && isWorksEntity.value) return props.placeholderOverride;
  // #598 r5 (flag-on single-row only): live counts. Works honors the xpac toggle —
  // core count normally, the /entities (xpac-inclusive) count when xpac is on.
  if (props.singleRow) {
    const count = isWorksEntity.value
      ? (isXpacEnabled.value ? entityCounts.value.works : worksCoreCount.value)
      : entityCounts.value[entityType.value];
    const compact = compactCount(count);
    if (compact) {
      if (isWorksEntity.value) return `Search ${compact} scholarly works`;
      const config = getEntityConfig(entityType.value);
      const base = config?.placeholder || `Search ${entityType.value}`;
      // All placeholders read "Search <noun phrase>" — inject the count after "Search".
      if (base.startsWith('Search ')) return `Search ${compact} ${base.slice(7)}`;
      return `${base} (${compact})`;
    }
  }
  if (!isWorksEntity.value) {
    const config = getEntityConfig(entityType.value);
    return config?.placeholder || `Search ${entityType.value}`;
  }
  return 'Search 480M scholarly works';
});

// When switching to semantic, force field and stemming
watch(searchMode, (val) => {
  if (val === 'semantic') {
    searchField.value = 'title_abstract_keywords';
    stemmingDisabled.value = false;
  }
});

// Menu actions that auto-submit when there's a search term
function setField(value) {
  searchField.value = value;
  submitIfHasSearch();
}

function setMode(value) {
  searchMode.value = value;
  nextTick(() => {
    if (searchString.value) {
      submitSearch();
    } else if (value === 'semantic') {
      // No search term yet — still clean up incompatible filters/group_by
      cleanupForSemanticMode();
    }
  });
}

function cleanupForSemanticMode() {
  const semanticAllowedKeys = new Set(
    facetConfigs('works')
      .filter(c => c.semanticSearchAllowed && c.entityToFilter === 'works')
      .map(c => c.key)
  );

  const currentQuery = { ...route.query };
  let changed = false;

  // Strip incompatible filters
  if (currentQuery.filter) {
    const currentFilters = filtersFromUrlStr('works', currentQuery.filter);
    const compatible = currentFilters.filter(f => semanticAllowedKeys.has(f.key));
    if (compatible.length !== currentFilters.length) {
      currentQuery.filter = filtersAsUrlStr(compatible) || undefined;
      changed = true;
    }
  }

  // Clear group_by (not supported in semantic search)
  if (currentQuery.group_by) {
    delete currentQuery.group_by;
    changed = true;
  }

  if (changed) {
    url.pushToRoute(router, {
      name: route.name,
      params: route.params,
      query: currentQuery,
    });
  }
}

function disableStemming(value) {
  stemmingDisabled.value = value;
  nextTick(() => submitIfHasSearch());
}

function submitIfHasSearch() {
  if (searchString.value) {
    nextTick(() => submitSearch());
  }
}

// Autocomplete
let fetchId = 0;
// Abort the previous keystroke's in-flight requests so responses can't pile up
// or arrive out of order (the fetchId guard below is the belt to this suspender).
let fetchAbort = null;

function entityIcon(type) {
  return getEntityConfig(type)?.icon || 'mdi-tag-outline';
}

function countCompleteWords(str) {
  // A "complete word" is a word followed by whitespace or another word.
  // "machine learning" = 1 complete word, "machine learning " = 2, "machine learning m" = 2
  const trimmed = str.trimStart();
  if (!trimmed) return 0;
  const words = trimmed.split(/\s+/);
  // If string ends with whitespace, all words are complete
  if (/\s$/.test(str)) return words.length;
  // Otherwise the last word is incomplete, so complete = total - 1
  return Math.max(0, words.length - 1);
}

// latinize / authorNameMatchesQuery / dedupeByName moved to searchBox.helpers.js
// (pure + unit-tested there, oxjob #820).

function topByWorksCount(items, n) {
  return [...items].sort((a, b) => (b.works_count || 0) - (a.works_count || 0)).slice(0, n);
}

// Edge autocomplete (oxjob #1529, edge autocomplete): entity rows on EVERY keystroke, no debounce, answered from
// index nodes the browser already holds or by the Cloudflare edge (~15 ms in Paris instead of ~310). The debounced
// fetchSuggestions below then adds work titles on the front page, or runs today's path when the edge had nothing.
// Rows keep the edge's ranking (judged better than today's sort by works count, #1504).
let edgeId = 0;
let edgeLast = { q: null, p: null };
const edgeEntity = () => (entityType.value === 'works' ? 'mix' : entityType.value);
function edgeItems(rows) {
  return dedupeByName(rows).map(r => {
    const type = r.short_id ? r.short_id.split('/')[0] : entityType.value;
    return { ...r, _acType: type, _icon: entityIcon(type) };
  });
}
async function fetchEdge(query) {
  const ent = edgeEntity();
  if (!edgeOn() || !edgeSupports(ent)) return;
  const id = ++edgeId;
  const p = edgeRows(ent, query);
  edgeLast = { q: query, p };
  const rows = await p;
  if (id !== edgeId || !rows) return;
  // keep work-title rows the debounced path already added for this same text
  const works = suggestions.value.filter(s => s._acType === 'works' && s._q === query);
  suggestions.value = [...edgeItems(rows).slice(0, works.length ? 3 : 5), ...works].slice(0, 5);
  highlightedIndex.value = -1;
  dropdownOpen.value = suggestions.value.length > 0;
}

async function searchEntities(entityType, query, signal) {
  // Use the search endpoint for full-text matching (not just prefix).
  // Fetch extra results so we can filter/re-rank client-side by works_count.
  const resp = await api.get(`/${entityType}`, {
    search: query,
    per_page: 10,
    select: 'id,display_name,works_count',
  }, signal ? { signal } : undefined);
  return resp.results || [];
}

// Look up a source (journal) by ISSN so an ISSN-shaped query surfaces the
// matching journal directly in the dropdown (zd#8095), the way DOI/ORCID jump
// to their entity. Returns the source result or null.
async function lookupSourceByIssn(issn, signal) {
  try {
    const resp = await api.get('/sources', {
      filter: `issn:${issn}`,
      per_page: 1,
      select: 'id,display_name,works_count',
    }, signal ? { signal } : undefined);
    return (resp.results && resp.results[0]) || null;
  } catch (e) {
    return null;
  }
}

async function fetchSuggestions(query) {
  if (!query || query.length === 0) {
    suggestions.value = [];
    dropdownOpen.value = false;
    return;
  }
  const id = ++fetchId;
  fetchAbort?.abort();
  const abortCtrl = new AbortController();
  fetchAbort = abortCtrl;
  const signal = abortCtrl.signal;
  const currentEntity = entityType.value;
  const config = getEntityConfig(currentEntity);

  // Kick off an ISSN→journal lookup in parallel; merged into the dropdown below.
  const issn = extractIssn(query);
  const issnSourcePromise = issn ? lookupSourceByIssn(issn, signal) : null;

  const tag = (items, type) =>
    dedupeByName(items || []).map(item => ({
      ...item,
      _acType: type,
      _icon: entityIcon(type),
    }));

  let edgeDone = false;
  if (edgeOn() && edgeSupports(edgeEntity())) {
    const rows = edgeLast.q === query ? await edgeLast.p : await edgeRows(edgeEntity(), query);
    if (id !== fetchId) return;
    if (rows) {
      edgeDone = true;
      let items = edgeItems(rows);
      const includeWorks = currentEntity === 'works' && (countCompleteWords(query) >= 3 ||
        (intent.value?.label === 'title' && intent.value.confidence >= 0.6));
      if (includeWorks) {
        let works = [];
        try {
          works = await api.getAutocomplete('works', { q: query }, { signal });
        } catch (e) {
          if (e?.code === 'ERR_CANCELED') return;
        }
        if (id !== fetchId) return;
        const w = tag(works, 'works').map(x => ({ ...x, _q: query }));
        items = [...items.slice(0, Math.max(3, 5 - w.length)), ...w].slice(0, 5);
      }
      suggestions.value = items.slice(0, 5);
    }
  }

  if (edgeDone) {
    // the edge answered; today's lookups are not needed
  } else if (currentEntity === 'works') {
    // Works SERP: suggest authors, institutions, keywords (as filters) + work
    // titles. All four come back in ONE request via the GUI-only combo endpoint
    // (see api.getFrontpageAutocomplete) — firing four parallel calls per
    // keystroke-pause tripped the anon 10 req/s cap and popped the credit-limit
    // modal mid-typing. Fails soft to empty lists; aborts with the stale signal.
    // A pasted title wants work suggestions however many words it has (intent, #1347).
    const includeWorks = countCompleteWords(query) >= 3 ||
      (intent.value?.label === 'title' && intent.value.confidence >= 0.6);
    let combo;
    try {
      combo = await api.getFrontpageAutocomplete(query, { includeWorks, signal });
    } catch (e) {
      if (e?.code === "ERR_CANCELED") return;
      combo = { authors: [], institutions: [], keywords: [], works: [] };
    }
    if (id !== fetchId) return;

    let authors = tag(combo.authors, 'authors');
    authors = authors.filter(a => authorNameMatchesQuery(a.display_name, query));
    let institutions = tag(combo.institutions, 'institutions');
    let keywords = tag(combo.keywords, 'keywords');
    let works = includeWorks ? tag(combo.works, 'works') : [];

    suggestions.value = topByWorksCount([...authors, ...institutions, ...keywords, ...works], 5);
  } else if (!config?.hasAutocomplete) {
    // No autocomplete for this entity type
    suggestions.value = [];
    dropdownOpen.value = false;
    return;
  } else if (config?.isNative) {
    // Native entities (authors, institutions, sources, publishers, funders, etc.)
    let results;
    try {
      results = await searchEntities(currentEntity, query, signal);
    } catch (e) {
      if (e?.code === 'ERR_CANCELED') return;
      throw e;
    }
    if (id !== fetchId) return;
    let items = tag(results, currentEntity);
    if (currentEntity === 'authors') {
      items = items.filter(a => authorNameMatchesQuery(a.display_name, query));
    }
    suggestions.value = topByWorksCount(items, 5);
  } else {
    // Non-native entities with autocomplete (keywords, topics, subfields, etc.)
    let results;
    try {
      results = await api.getAutocomplete(currentEntity, { q: query }, { signal });
    } catch (e) {
      if (e?.code === 'ERR_CANCELED') return;
      throw e;
    }
    if (id !== fetchId) return;
    suggestions.value = tag(results || [], currentEntity).slice(0, 5);
  }

  // If the query was an ISSN and it resolved to a journal, surface that journal
  // as the top suggestion (selecting it routes to the source page).
  if (issnSourcePromise) {
    const src = await issnSourcePromise;
    if (id !== fetchId) return;
    if (src) {
      const item = { ...src, _acType: 'sources', _icon: entityIcon('sources') };
      suggestions.value = [item, ...suggestions.value.filter(s => s.id !== item.id)].slice(0, 5);
    }
  }

  highlightedIndex.value = -1;
  dropdownOpen.value = suggestions.value.length > 0;
}

const debouncedFetch = debounce(fetchSuggestions, 100);

watch(searchString, (val) => {
  if (!isUserTyping.value) return;
  intent.value = val ? classifyQuery(val) : null;
  if (val && suggestionsOff.value) {
    // A question (oxjob #1744): no fetch, and drop any in flight so it can't reopen the dropdown.
    dismissDropdown();
    edgeId++;
  } else if (val) {
    fetchEdge(val);
    debouncedFetch(val);
  } else {
    debouncedFetch.cancel();
    suggestions.value = [];
    dropdownOpen.value = false;
  }
});

// A hidden list keeps no highlight, so Enter on a question submits the text (oxjob #1744).
watch(suggestionsOff, (off) => {
  if (off) highlightedIndex.value = -1;
});

function dismissDropdown() {
  // Kill anything in flight (#820 r4): a pending debounced fetch — or a slow
  // response landing after dismissal — would silently reopen the dropdown
  // after a submit/blur/route change, and could flip the footer's target
  // between two quick Enters.
  debouncedFetch.cancel();
  fetchAbort?.abort();
  fetchId++;
  dropdownOpen.value = false;
  highlightedIndex.value = -1;
}

function selectSuggestion(item) {
  isUserTyping.value = false;
  dismissDropdown();
  searchString.value = '';

  // Non-native ids are namespaced (https://openalex.org/keywords/kelp), so
  // stripping only the domain double-prefixes the route (/keywords/keywords%2Fkelp).
  const entityId = getShortId(item.id) || item.id;
  router.push({ name: 'EntityPage', params: { entityType: item._acType, entityId } });
}

function onEnter() {
  if (thinking.value) return;
  if (highlightedIndex.value >= 0 && highlightedIndex.value < suggestions.value.length) {
    selectSuggestion(suggestions.value[highlightedIndex.value]);
  } else if (showDropdown.value) {
    // The dropdown footer is the visible default — Enter runs what it shows
    // (#820 r3). Only when the footer is on screen; with no dropdown (or
    // suggestions still loading) Enter keeps its plain current-entity search,
    // so behavior never depends on invisible state.
    submitFooter();
  } else {
    dismissDropdown();
    submitSearch();
  }
}

// The dropdown-footer action (#820 r3): the full SERP search the footer
// advertises. Same-entity → the normal submit path (keeps identifier/OQL
// interception and works search modes). Entity-swapped (all suggestions were
// authors/sources/institutions) → a clean search on that entity's SERP, with
// no carried-over filters — the same destination as switching the entity
// selector and pressing Enter.
function submitFooter() {
  const target = footerEntityType.value;
  dismissDropdown();
  if (target !== entityType.value) {
    const q = searchString.value.trim();
    isUserTyping.value = false;
    searchString.value = '';
    url.pushToRoute(router, {
      name: 'Serp',
      params: { entityType: target },
      query: { search: q, page: 1, sort: 'relevance_score:desc' },
    });
    return;
  }
  submitSearch();
}

function onArrowDown() {
  if (!showDropdown.value) return;
  highlightedIndex.value = Math.min(highlightedIndex.value + 1, suggestions.value.length - 1);
}

function onArrowUp() {
  if (!showDropdown.value) return;
  highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
}

function onEscape() {
  if (showDropdown.value) {
    dismissDropdown();
  }
}

function onTextareaInput() {
  isUserTyping.value = true;
  dbSearchError.value = '';
  resizeTextarea();
}

// A paste updates the v-model value but, unlike a keystroke, does not run our
// @input handler — so isUserTyping never flips and the suggestion watcher bails
// (zd#8095: pasting an ISSN/term fired no lookup until you pressed a key). Flag
// it as user input and kick the fetch once the pasted text has landed.
function onTextareaPaste() {
  isUserTyping.value = true;
  nextTick(async () => {
    resizeTextarea();
    // Pasted OQL routes straight to the OQL tab (#598) — don't autocomplete it.
    if (await tryOqlRoute()) return;
    // A pasted PubMed / Scopus / WoS ... search opens translated (#1659).
    if (tryDatabaseSearchRoute()) return;
    if (searchString.value && !suggestionsOff.value) debouncedFetch(searchString.value);
  });
}

function resizeTextarea() {
  nextTick(() => {
    const el = inputRef.value;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  });
}

function onFocus() {
  isFocused.value = true;
  ensureIntentModel();   // lazy, idempotent: fetch the classifier weights on first focus (#1347)
  edgeWarm(edgeEntity()); // edge autocomplete: open the connection to the edge before the first keystroke
  if (suggestions.value.length > 0 && searchString.value) {
    dropdownOpen.value = true;
  }
}

function onBlur() {
  isFocused.value = false;
  dismissDropdown();
}

onBeforeUnmount(() => {
  debouncedFetch.cancel();
});

// Populate from URL on mount and route changes
function syncFromRoute() {
  isUserTyping.value = false;
  const searchFromRoute = url.getSearchFromRoute(route);
  if (searchFromRoute) {
    searchString.value = searchFromRoute.value;
    const type = searchFromRoute.type;

    if (type === 'search.semantic') {
      searchMode.value = 'semantic';
      searchField.value = 'title_abstract_keywords';
      stemmingDisabled.value = false;
    } else if (type === 'search.title.exact') {
      searchMode.value = 'term';
      searchField.value = 'title';
      stemmingDisabled.value = true;
    } else if (type === 'search.title') {
      searchMode.value = 'term';
      searchField.value = 'title';
      stemmingDisabled.value = false;
    } else if (type === 'search.title_and_abstract.exact') {
      searchMode.value = 'term';
      searchField.value = 'title_and_abstract';
      stemmingDisabled.value = true;
    } else if (type === 'search.title_and_abstract') {
      searchMode.value = 'term';
      searchField.value = 'title_and_abstract';
      stemmingDisabled.value = false;
    } else if (type === 'search.title_abstract_keywords.exact') {
      searchMode.value = 'term';
      searchField.value = 'title_abstract_keywords';
      stemmingDisabled.value = true;
    } else if (type === 'search.title_abstract_keywords') {
      searchMode.value = 'term';
      searchField.value = 'title_abstract_keywords';
      stemmingDisabled.value = false;
    } else if (type === 'search.exact') {
      searchMode.value = 'term';
      searchField.value = 'all';
      stemmingDisabled.value = true;
    } else {
      // 'search' (default)
      searchMode.value = 'term';
      searchField.value = 'all';
      stemmingDisabled.value = false;
    }
  } else {
    searchString.value = '';
  }
}

onMounted(() => {
  syncFromRoute();
  resizeTextarea();
  // A landing empty-Enter asked the next SERP's box to grab focus (#598 r4).
  if (consumeSearchBoxFocus()) focusSearchInput();
});
watch(() => route.fullPath, () => {
  dismissDropdown();
  syncFromRoute();
  resizeTextarea();
});
// On `/q?oql=` the search lives only in the settled response's canonical x_query
// (url.getSearchFromRoute reads it once the fetch has landed), so re-hydrate when
// the response settles — never while the user is typing or has the box focused
// (oxjob #1245, Inist 2.7).
watch(
  () => [store.state.resultsObject?.meta?.x_query?.url, store.state.isLoading],
  () => {
    if (!route.query.oql || store.state.isLoading) return;
    if (isUserTyping.value || isFocused.value) return;
    syncFromRoute();
    resizeTextarea();
  },
);

// The homepage question, signed in (`nl_search`, #1744; Jason, 2026-10-21): stay on the
// landing page until the model has written the query, then open the page on it. Navigating
// first showed a few seconds of all works while the model thought, which read as the answer.
// The chat on the next page picks up the saved thread (question + answer) and asks nothing.
const thinking = ref(false);
const thinkElapsed = ref(0);
let thinkTimer = null;
let left = false;
function stopThinkClock() {
  if (thinkTimer) clearInterval(thinkTimer);
  thinkTimer = null;
}
onBeforeUnmount(() => { left = true; stopThinkClock(); });

async function askOnHome(text) {
  if (thinking.value || !text) return;
  thinking.value = true;
  dismissDropdown();
  thinkElapsed.value = 0;
  const t0 = Date.now();
  thinkTimer = setInterval(() => { thinkElapsed.value = Math.floor((Date.now() - t0) / 1000); }, 500);
  const from = router.currentRoute.value.fullPath;
  let msg;
  try {
    await waitForApiKey(store);
    msg = await answerQuestion({ text, currentQuery: null, history: [] });
  } finally {
    stopThinkClock();
    thinking.value = false;
  }
  // Left the page while waiting: don't pull them back to the answer.
  if (left || router.currentRoute.value.fullPath !== from) return;
  // A new question from the homepage starts its own conversation, as `?ask=` does.
  saveThread([{ role: 'user', text }, msg]);
  if (msg.shownOql) {
    // The new page opens on the chart / table the answer suits (NlSerp reads this once).
    try { sessionStorage.setItem('nlSearch.freshAnswer', msg.shownOql); } catch (e) { /* private mode */ }
    url.pushToRoute(router, { name: 'OqlQuery', query: { oql: oqlForPageUrl(msg.shownOql) } });
  } else {
    // A help-desk answer or an error: the chat shows it, over the plain page.
    url.pushToRoute(router, { name: 'Serp', params: { entityType: 'works' } });
  }
}

async function submitSearch(forceEntityType) {
  if (thinking.value) return;
  const targetEntityType = forceEntityType || entityType.value;

  if (!searchString.value) {
    if (props.showExamples) {
      // Legacy landing box: empty Enter opens the entity's SERP (unchanged).
      url.pushToRoute(router, { name: 'Serp', params: { entityType: targetEntityType } });
    } else if (props.singleRow && (route.name === 'Home' || route.name === 'HomeV2')) {
      // #598 r4: flag-on landing bar — empty Enter opens the entity's SERP in
      // Basic mode with the search box focused, ready to type. (#681: HomeV2 is
      // the landing revision's test route, same behavior.)
      store.commit('setSerpModeOverride', 'basic');
      requestSearchBoxFocus();
      url.pushToRoute(router, { name: 'Serp', params: { entityType: targetEntityType } });
    }
    return;
  }

  // A search strategy from another database: translated locally, before any lookup (#1659)
  if (tryDatabaseSearchRoute()) return;

  // Intercept DOI/ORCID before regular search
  const handled = await tryIdentifierLookup();
  if (handled) return;

  // Typed-not-pasted OQL submitted with Enter/magnifier also routes (#598).
  if (await tryOqlRoute()) return;

  // The new search (`nl_search`, oxjob #1744): a question typed on the homepage opens the chat
  // with it instead of running as keywords (plain keywords still answer there, as a search).
  // DOIs, ORCIDs, database strategies and OQL above route as before; no other box changes.
  if (store.getters.nlSearch && route.name === 'Home') {
    if (store.getters['user/userId']) await askOnHome(searchString.value.trim());
    else url.pushToRoute(router, { name: 'Serp', params: { entityType: 'works' }, query: { ask: searchString.value } });
    return;
  }

  // For semantic search, do a single route push that also cleans incompatible filters/group_by
  if (searchMode.value === 'semantic') {
    const semanticAllowedKeys = new Set(
      facetConfigs('works')
        .filter(c => c.semanticSearchAllowed && c.entityToFilter === 'works')
        .map(c => c.key)
    );
    const currentQuery = { ...route.query };
    // Remove all search params
    ['search', 'search.exact', 'search.semantic', 'search.title', 'search.title.exact',
     'search.title_and_abstract', 'search.title_and_abstract.exact',
     'search.title_abstract_keywords', 'search.title_abstract_keywords.exact'].forEach(k => delete currentQuery[k]);
    // Under `/q?oql=` the chips live in the canonical query, not the URL, and a
    // stale `oql=` would make the router bounce this back to `/q` (oxjob #1245).
    if (currentQuery.oql) {
      delete currentQuery.oql;
      currentQuery.filter = url.chipFilterStr(route);
    }
    currentQuery['search.semantic'] = searchString.value;
    currentQuery.page = 1;
    currentQuery.sort = 'relevance_score:desc';
    delete currentQuery.group_by;
    // Strip incompatible filters
    if (currentQuery.filter) {
      const currentFilters = filtersFromUrlStr('works', currentQuery.filter);
      const compatible = currentFilters.filter(f => semanticAllowedKeys.has(f.key));
      currentQuery.filter = filtersAsUrlStr(compatible) || undefined;
    }
    url.pushToRoute(router, {
      name: 'Serp',
      params: { entityType: targetEntityType },
      query: currentQuery,
    });
    return;
  }

  url.setNewSearch(targetEntityType, resolvedSearchType.value, searchString.value);
}

function clearSearch() {
  searchString.value = '';
  url.clearNewSearch();
  inputRef.value?.focus();
}

// The single-row magnifier button (#440 r5): plain submit, ignoring any
// autocomplete highlight (clicking the button means "search what I typed").
function clickSubmit() {
  dismissDropdown();
  submitSearch();
}

// Expose submit() so a host page (e.g. the #681 landing) can drive search from
// its own "Search" call-to-action button, replacing the in-box magnifier.
// askText(): a suggested query clicked under the box (#1744) types itself in and runs.
function askText(text) {
  if (thinking.value || !text) return;
  isUserTyping.value = false;
  searchString.value = text;
  clickSubmit();
}
defineExpose({ submit: clickSubmit, askText, thinking });

// Vuetify's v-menu moves focus (to its activator, then <body>) while it
// closes, which clobbers a single focus() call. Retry over a short window
// until focus actually sticks on the input.
function focusSearchInput() {
  const el = inputRef.value;
  if (!el) return;
  const deadline = Date.now() + 500;
  const tryFocus = () => {
    if (document.activeElement === el) return;
    el.focus();
    if (document.activeElement !== el && Date.now() < deadline) {
      setTimeout(tryFocus, 50);
    }
  };
  nextTick(tryFocus);
}
</script>

<style lang="scss" scoped>
.db-search-error {
  border-top: 1px solid rgba(var(--v-theme-error), 0.3);
  background: rgba(var(--v-theme-error), 0.04);
  border-radius: 0 0 16px 16px;
}
.search-box {
  position: relative;
  border: 1px solid #E0E0E0;
  border-radius: 16px;
  background: white;
  transition: border-color 0.15s ease;

  &--focused {
    border-color: #000;
  }

  /* Embedded as a host card's body: no border/radius/background of its own, and no
     focus border (the host card owns the chrome). */
  &--borderless {
    border-color: transparent !important;
    border-radius: 0;
    background: transparent;
  }
}

.search-row-1 {
  min-height: 52px;
}

/* Single row (#440 r5): ~20% shorter than the original 52px so it reads as a
   text field, with the magnifier submit at the right edge. */
.search-row-single {
  min-height: 42px;
  padding: 2px 4px 2px 6px;
  gap: 2px;
}
.search-box--single .search-input {
  padding: 8px 10px;
}
/* Two-deck variant (#681 landing R22, Claude.ai-style): grid re-lays the shared
   single-row children — row 1 is the bare input (+ clear), row 2 is entity
   selector left, xpac pill / kebab / Search button right. */
.search-row-deck {
  display: grid;
  grid-template-columns: auto 1fr auto auto auto;
  grid-template-areas:
    "q q q q q"
    "e . x k s";
  align-items: center;
  row-gap: 2px;
  padding: 6px 8px 8px;
}
/* right padding clears the absolutely-positioned clear button */
.search-row-deck .search-input { grid-area: q; width: 100%; padding: 10px 40px 6px 10px; }
.search-row-deck .clear-btn { position: absolute; top: 9px; right: 10px; }
.search-row-deck .entity-slot { grid-area: e; justify-self: start; }
.search-row-deck .xpac-pill { grid-area: x; }
.search-row-deck .kebab-slot { grid-area: k; }
.deck-submit {
  grid-area: s;
  font: inherit; font-size: 14px; font-weight: 600; line-height: 1;
  color: #fff; background: #1a1a1a;
  border: 1px solid #000; border-radius: 8px; /* darker outline than the fill */
  padding: 9px 18px; margin-left: 6px; cursor: pointer;
  transition: background .12s ease;
  &:hover { background: #000; }
}
.deck-submit:disabled { cursor: default; background: #1a1a1a; min-width: 82px; }
/* The homepage's wait for the model's query (#1744): row 2, left, in place of the entity
   selector. Dots as in the chat (NlChat .nl-dots). */
.search-row-deck .nl-wait { grid-column: 1 / 3; grid-row: 2; }
.nl-wait {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}
.nl-wait-more {
  display: block;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}
.nl-dots {
  display: inline-flex;
  gap: 3px;
}
.nl-dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  opacity: 0.35;
  animation: nl-pulse 1.2s infinite ease-in-out;
}
.nl-dots span:nth-child(2) { animation-delay: 0.2s; }
.nl-dots span:nth-child(3) { animation-delay: 0.4s; }
@keyframes nl-pulse {
  0%, 80%, 100% { opacity: 0.25; transform: scale(0.85); }
  40% { opacity: 1; transform: scale(1); }
}
/* Entity selector chip matches the filter chips' height (32px, v-chip
   size=default) — it rendered shorter than the chips below it (#440 r5). */
.search-row-single :deep(.entity-chip) {
  height: 32px;
  font-size: 0.875rem;
  padding: 0 12px;
  /* r4: ghost button — no fill at rest, subtle fill on hover. Out-specifies the
     component's own .entity-chip !important background. */
  background: transparent !important;
}
.search-row-single :deep(.entity-chip:hover) {
  background: rgba(0, 0, 0, 0.05) !important;
}
.xpac-pill {
  flex: 0 0 auto;
}

.search-divider {
  height: 1px;
  background-color: #F0F0F0;
  margin: 0 16px;
}

.search-row-2 {
  min-height: 40px;
  padding: 4px 8px;
}

.search-input {
  border: none;
  outline: none;
  font-size: 16px;
  line-height: 1.5;
  padding: 14px 18px;
  background: transparent;
  font-family: Inter, sans-serif;
  min-width: 0;
  resize: none;
  max-height: 480px; /* ~20 lines */
  overflow-y: auto;

  &::placeholder {
    color: #9CA3AF;
  }
}

.control-btn {
  color: #6B7280 !important;

  &:hover {
    color: #374151 !important;
  }
}

.control-label {
  font-size: 13px;
  font-weight: 500;
}

.control-btn--disabled {
  opacity: 0.5 !important;
  cursor: default !important;
}

.menu-subtitle {
  font-size: 12px !important;
  color: #4B5563 !important;
}

/* #598 r1: the narrowed kebab list — subtitles may wrap to two lines (Vuetify
   clamps list subtitles to one line by default), and the row toggles are inert
   (the whole row is the click target, so a tap on the switch can't double-fire). */
.search-kebab-list :deep(.v-list-item-subtitle) {
  -webkit-line-clamp: 2;
  white-space: normal;
}
.search-kebab-list :deep(.v-list-item-title) {
  white-space: normal; /* titles too — long titles wrap, never truncate */
}
.search-kebab-list :deep(.kebab-switch) {
  pointer-events: none;
  flex: 0 0 auto;
  /* r2: Vuetify's compact inset switch is huge in a dense menu — scale it down.
     transform (not track/thumb sizing) so the thumb's on/off travel stays right. */
  transform: scale(0.65);
  transform-origin: center right;
}
.search-kebab-list :deep(.kebab-switch .v-selection-control) {
  min-height: 0;
}
/* r2.1: stock Vuetify paints the OFF track as low-opacity grey — invisible on the
   white menu. Give both states solid, explicit tracks (iOS/Linear style): off =
   mid-grey, on = near-black; thumb = white with a hairline shadow in both. */
.search-kebab-list :deep(.kebab-switch .v-switch__track) {
  background-color: #D1D5DB;
  opacity: 1;
}
.search-kebab-list :deep(.kebab-switch .v-selection-control--dirty .v-switch__track) {
  background-color: #374151;
}
.search-kebab-list :deep(.kebab-switch .v-switch__thumb) {
  background-color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
}

.check-icon {
  color: #1a1a1a !important;
  font-weight: 900;
}

.autocomplete-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  margin-top: 4px;
  background: white;
  border: 1px solid #E0E0E0;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  z-index: 100;
  overflow: hidden;
}

.autocomplete-list {
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 0;
}

.autocomplete-item {
  padding: 8px 18px;
  cursor: pointer;
  transition: background-color 0.1s ease;

  /* Hover feedback is CSS-only (#820 r4): hover must NOT set highlightedIndex.
     When the dropdown renders under a stationary cursor, Chrome fires a
     synthetic mouseenter, and Enter would then "select" a suggestion the user
     never touched. Enter honors only the keyboard (arrow-key) highlight; the
     mouse selects by clicking. */
  &:hover,
  &--highlighted {
    background-color: #F3F4F6;
  }
}

.autocomplete-item-icon {
  color: #9CA3AF;
  flex-shrink: 0;
  margin-right: 10px;
}

.autocomplete-item-name {
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  line-height: 1.4;
}

/* The dropdown footer (#820 r3): the full SERP search, echoing what Enter does.
   Medium-emphasis, hairline-separated, with an "enter" keycap on the right. */
.autocomplete-footer {
  padding: 8px 18px;
  cursor: pointer;
  border-top: 1px solid #F3F4F6;
  transition: background-color 0.1s ease;

  &:hover {
    background-color: #F3F4F6;
  }
}

.autocomplete-footer-text {
  font-size: 14px;
  color: #6B7280;
  line-height: 1.4;
  flex-grow: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  em {
    color: #374151;
  }
}

.enter-keycap {
  flex-shrink: 0;
  margin-left: 12px;
  font-size: 11px;
  line-height: 18px;
  color: #9CA3AF;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  padding: 0 6px;
}

</style>
